import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { useAuth } from '../context/AuthContext';

// Toast Notification Component
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 3000 }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
  }[type];

  const icon = {
    success: '✓',
    error: '✗',
    info: 'ℹ',
  }[type];

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 min-w-80`}>
        <span className="text-xl">{icon}</span>
        <span className="flex-1">{message}</span>
        <button onClick={onClose} className="text-white hover:text-gray-200 text-lg">
          ×
        </button>
      </div>
    </div>
  );
};

// Application Question Interface
interface ApplicationQuestion {
  id?: number;
  question: string;
  is_default: boolean;
  is_important?: boolean;
  created_at?: string;
  updated_at?: string;
}

// Define the type for location state
interface LocationState {
  type: 'administrator' | 'underwriter';
  insurerId: number;
  insurerName: string;
  insurerStatus: string;
  category: string;
}

const ApplicationQuestions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Safe destructuring
  const type          = location.state?.type          as 'administrator' | 'underwriter' | undefined;
  const insurerId     = location.state?.insurerId     as number | undefined;
  const insurerName   = location.state?.insurerName   as string | undefined;
  const insurerStatus = location.state?.insurerStatus as string | undefined;
  const category      = location.state?.category      as string | undefined;

  // Cache stable values so they survive re-renders / StrictMode remounts
const [stableInsurerId, setStableInsurerId] = useState<number | undefined>(undefined);
const [stableCategory, setStableCategory] = useState<string | undefined>(undefined);

// ADD THIS useEffect to debug the location state
useEffect(() => {
  console.log('DEBUG - Location state received:', location.state);
  console.log('DEBUG - Destructured values:', {
    type,
    insurerId,
    insurerName,
    insurerStatus,
    category
  });
}, [location.state, type, insurerId, insurerName, insurerStatus, category]);

useEffect(() => {
  console.log('DEBUG - Setting stable values from:', {
    insurerId,
    category,
    hasLocationState: !!location.state
  });
  
  // Always set stable values from location.state directly to avoid timing issues
  if (location.state) {
    const state = location.state as LocationState;
    if (state.insurerId !== undefined && state.insurerId !== stableInsurerId) {
      console.log('DEBUG - Setting stableInsurerId to:', state.insurerId);
      setStableInsurerId(state.insurerId);
    }
    if (state.category !== undefined && state.category !== stableCategory) {
      console.log('DEBUG - Setting stableCategory to:', state.category);
      setStableCategory(state.category);
    }
  }
}, [location.state, stableInsurerId, stableCategory]);

  // One-time guard with delay to survive StrictMode remount
// One-time guard – skip StrictMode double-run & dev remounts
const hasCheckedRedirect = useRef(false);

useEffect(() => {
  // Skip if already checked
  if (hasCheckedRedirect.current) return;

  hasCheckedRedirect.current = true;

  // Wait for location state to be available
  const timer = setTimeout(() => {
    console.log('Guard check — location.state:', location.state);
    
    // Check if we have the required state
    if (!location.state || !location.state.insurerId) {
      console.error('DEBUG - Missing required state:', location.state);
      showToast('Error: No insurer selected. Please go back and select an insurer.', 'error');
      navigate('/application-insurers', { replace: true });
      return;
    }

    const state = location.state as LocationState;
    console.log('DEBUG - State validated:', {
      insurerId: state.insurerId,
      category: state.category
    });
    
  }, 150); // Increase delay to ensure state is loaded

  return () => clearTimeout(timer);
}, [location.state, navigate]);

useEffect(() => {
  const effectiveInsurerId = stableInsurerId || insurerId;
  const effectiveCategory = stableCategory || category;
  
  if (effectiveInsurerId && effectiveCategory) {
    console.log('DEBUG - Fetching questions for:', {
      effectiveInsurerId,
      effectiveCategory,
      stableInsurerId,
      insurerId
    });
    fetchApplicationQuestions();
  }
}, [stableInsurerId, insurerId, stableCategory, category]);

useEffect(() => {
  const state = location.state as LocationState;
  const effectiveInsurerId = state?.insurerId || stableInsurerId || insurerId;
  const effectiveCategory = state?.category || stableCategory || category;
  
  if (effectiveInsurerId && effectiveCategory) {
    console.log('DEBUG - Fetching questions with:', {
      effectiveInsurerId,
      effectiveCategory,
      hasLocationState: !!location.state
    });
    fetchApplicationQuestions();
  } else {
    console.log('DEBUG - Not fetching yet, missing:', {
      effectiveInsurerId,
      effectiveCategory,
      locationState: location.state
    });
  }
}, [location.state, stableInsurerId, insurerId, stableCategory, category]);

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [questions, setQuestions] = useState<ApplicationQuestion[]>([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<{ id: number; question: string } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
  };

  const closeToast = () => {
    setToast(null);
  };

  const fetchApplicationQuestions = async () => {
  // Get values directly from location.state
  const state = location.state as LocationState;
  const effectiveInsurerId = state?.insurerId || stableInsurerId || insurerId;
  const effectiveCategory = state?.category || stableCategory || category;
  
  console.log('DEBUG fetchApplicationQuestions:', {
    effectiveInsurerId,
    effectiveCategory,
    locationState: location.state
  });
  
  if (!effectiveInsurerId) {
    console.warn('Cannot fetch questions: No insurerId available');
    setIsLoading(false);
    return;
  }
  
  if (!effectiveCategory) {
    console.warn('Cannot fetch questions: No category available');
    setIsLoading(false);
    return;
  }
  
  setIsLoading(true);
  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    let endpoint: string;
    let params = new URLSearchParams();
    
    params.append('insurerId', effectiveInsurerId.toString());
    
    if (effectiveCategory === 'Debit Order') {
      endpoint = `${API_URL}/api/debitorderquestions`;
    } else if (effectiveCategory === 'Important') {
      endpoint = `${API_URL}/api/applicationimportant`;
    } else if (effectiveCategory === 'Fees') {
      endpoint = `${API_URL}/api/managementfees`;
    } else if (effectiveCategory === 'Declaration Quotes') {
      endpoint = `${API_URL}/api/application-quotes-questions/declaration?insurerId=${effectiveInsurerId}`;
    } else if (effectiveCategory === 'Service Provider') {
      endpoint = `${API_URL}/api/serviceproviderquestions`;
    } else if (effectiveCategory === 'Broker Consent') {
      endpoint = `${API_URL}/api/application/brokerconsentquestions`;
    } else if (effectiveCategory === 'Disclosure') {
      endpoint = `${API_URL}/api/application/disclosurequestions`;
    } else {
    // This is where Consent Clause should go
    endpoint = `${API_URL}/api/application/questions`;
    if (type) params.append('type', type);
    // Don't encode here - URLSearchParams will handle encoding
    params.append('category', effectiveCategory);
    console.log('🔍 Using standard application/questions endpoint for category:', effectiveCategory);
  }

    // Then only add params if we're not using Declaration Quotes
    if (effectiveCategory !== 'Declaration Quotes' && params.toString()) {
      endpoint += `?${params.toString()}`;
    }

    console.log('DEBUG - Fetching from:', endpoint);

    const response = await fetch(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.log(`Using default questions for ${effectiveCategory || 'unknown'}`);
      return;
    }

    const data = await response.json();
    let questionsData: ApplicationQuestion[] = [];
    
    // FIXED: Handle different response formats
    if (effectiveCategory === 'Fees') {
      // managementfees endpoint returns array directly
      questionsData = Array.isArray(data) ? data : [];
    } else if (data?.success && Array.isArray(data.data)) {
      questionsData = data.data;
    } else if (Array.isArray(data)) {
      questionsData = data;
    }
    
    setQuestions(questionsData);
  } catch (err: any) {
    console.error('Error fetching questions:', err);
  } finally {
    setIsLoading(false);
  }
};

  const handleAddQuestion = async () => {
  // Get values directly from location.state to avoid timing issues
  const state = location.state as LocationState;
  const effectiveInsurerId = state?.insurerId || stableInsurerId || insurerId;
  const effectiveCategory = state?.category || stableCategory || category;
  
  console.log('DEBUG handleAddQuestion - All values:', {
    locationState: location.state,
    stateInsurerId: state?.insurerId,
    stableInsurerId,
    insurerId,
    effectiveInsurerId,
    effectiveCategory
  });

  if (!newQuestion.trim()) {
    showToast('Please enter a question', 'error');
    return;
  }
  
  if (questions.some(q => q.question.toLowerCase() === newQuestion.toLowerCase().trim())) {
    showToast('This question already exists', 'error');
    return;
  }
  
  if (!effectiveInsurerId) {
    console.error('DEBUG - No insurerId found after all checks:', {
      locationState: location.state,
      stableInsurerId,
      insurerId,
      state: state
    });
    showToast('Cannot add question: No insurer selected. Please refresh the page or go back and select an insurer.', 'error');
    return;
  }
  
  if (!effectiveCategory) {
    console.error('DEBUG - No category found:', {
      locationState: location.state,
      stableCategory,
      category
    });
    showToast('Cannot add question: No category selected.', 'error');
    return;
  }

  setIsLoading(true);
  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    let endpoint: string;
    const body: any = {
      question: newQuestion.trim(),
      is_default: false,
      insurer_id: effectiveInsurerId,
    };

    if (effectiveCategory === 'Debit Order') {
      endpoint = `${API_URL}/api/debitorderquestions`;
    } else if (effectiveCategory === 'Important') {
      endpoint = `${API_URL}/api/applicationimportant`;
    } else if (effectiveCategory === 'Fees') {
      endpoint = `${API_URL}/api/managementfees`;
    } else if (effectiveCategory === 'Service Provider') {
      endpoint = `${API_URL}/api/serviceproviderquestions`;
    } else if (effectiveCategory === 'Declaration Quotes') {
      endpoint = `${API_URL}/api/application-quotes-questions/declaration`;
    } else if (effectiveCategory === 'Broker Consent') {
      endpoint = `${API_URL}/api/application/brokerconsentquestions`;
    } else if (effectiveCategory === 'Disclosure') {
      endpoint = `${API_URL}/api/application/disclosurequestions`;
    } else {
      endpoint = `${API_URL}/api/application/questions`;
      body.category = effectiveCategory;
    }

    console.log('DEBUG - Sending request to:', endpoint);
    console.log('DEBUG - Request body:', body);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (response.ok) {
      const data = await response.json();
      let savedQuestion;
      
      // FIXED: Handle different response formats
      if (effectiveCategory === 'Fees') {
        // managementfees endpoint returns the question directly
        savedQuestion = data;
      } else if (data?.success && data.data) {
        savedQuestion = data.data;
      } else if (data?.id) {
        savedQuestion = data;
      } else {
        const newId = Math.max(...questions.map(q => q.id || 0), 0) + 1;
        savedQuestion = { ...body, id: newId };
      }

      const finalQuestion: ApplicationQuestion = {
        id: savedQuestion.id,
        question: savedQuestion.question || newQuestion.trim(),
        is_default: savedQuestion.is_default || false,
        created_at: savedQuestion.created_at,
        updated_at: savedQuestion.updated_at,
      };

      setQuestions(prev => [...prev, finalQuestion]);
      showToast(`Question added successfully to ${effectiveCategory ?? 'the category'}!`);
      setNewQuestion('');
    } else {
      const errorText = await response.text();
      console.error('Failed to add question:', errorText);
      showToast('Failed to add question on server', 'error');
    }
  } catch (err: any) {
    console.error('Error adding question:', err);
    const newId = Math.max(...questions.map(q => q.id || 0), 0) + 1;
    setQuestions(prev => [...prev, {
      id: newId,
      question: newQuestion.trim(),
      is_default: false,
    }]);
    setNewQuestion('');
    showToast('Question added (offline mode)', 'info');
  } finally {
    setIsLoading(false);
  }
};

  const handleStartEdit = (question: ApplicationQuestion) => {
    setIsEditing(question.id || null);
    setEditQuestion(question.question);
  };

  const handleSaveEdit = async (id: number) => {
  if (!editQuestion.trim()) {
    showToast('Question cannot be empty', 'error');
    return;
  }
  if (questions.some(q => q.id !== id && q.question.toLowerCase() === editQuestion.toLowerCase().trim())) {
    showToast('Another question with this text already exists', 'error');
    return;
  }

  setIsLoading(true);
  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    let endpoint: string;
    
    // Get effective category
    const state = location.state as LocationState;
    const effectiveCategory = state?.category || stableCategory || category;
    
    if (effectiveCategory === 'Debit Order') {
      endpoint = `${API_URL}/api/debitorderquestions/${id}`;
    } else if (effectiveCategory === 'Fees') {
      endpoint = `${API_URL}/api/managementfees/${id}`;
    } else if (effectiveCategory === 'Service Provider') {
      endpoint = `${API_URL}/api/serviceproviderquestions/${id}`;
    } else if (effectiveCategory === 'Declaration Quotes') {
      endpoint = `${API_URL}/api/application-quotes-questions/declaration/${id}`;
    } else if (effectiveCategory === 'Broker Consent') {
      endpoint = `${API_URL}/api/brokerconsentquestions/${id}`;
    } else if (effectiveCategory === 'Disclosure') {
      endpoint = `${API_URL}/api/disclosurequestions/${id}`;
    } else {
      endpoint = `${API_URL}/api/application/questions/${id}`;
    }

    const updatedQuestion = {
      question: editQuestion.trim(),
    };

    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedQuestion),
    });

    if (!response.ok) throw new Error('Update failed');

    setQuestions(prev => prev.map(q =>
      q.id === id ? { ...q, question: editQuestion.trim() } : q
    ));

    setIsEditing(null);
    setEditQuestion('');
    showToast('Question updated successfully!');
  } catch (err) {
    console.error('Update error:', err);
    setQuestions(prev => prev.map(q =>
      q.id === id ? { ...q, question: editQuestion.trim() } : q
    ));
    setIsEditing(null);
    setEditQuestion('');
    showToast('Question updated (offline)', 'info');
  } finally {
    setIsLoading(false);
  }
};

  const handleCancelEdit = () => {
    setIsEditing(null);
    setEditQuestion('');
  };

  const handleSetDefault = async (id: number) => {
  setIsLoading(true);
  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    // Get effective values
    const state = location.state as LocationState;
    const effectiveInsurerId = state?.insurerId || stableInsurerId || insurerId;
    const effectiveCategory = state?.category || stableCategory || category;
    
    let endpoint: string;

    if (effectiveCategory === 'Fees') {
      endpoint = `${API_URL}/api/managementfees/${id}/set-default`;
    } else if (effectiveCategory === 'Service Provider') {
      endpoint = `${API_URL}/api/serviceproviderquestions/${id}/set-default`;
    } else {
      endpoint = `${API_URL}/api/application/questions/${id}/set-default`;
    }

    await fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ['insurer_id']: effectiveInsurerId,
        insurer_type: type ?? 'administrator',
        category: effectiveCategory ?? ''
      }),
    });

    setQuestions(prev => prev.map(q => ({
      ...q,
      is_default: q.id === id
    })));

    showToast('Default question updated successfully!');
  } catch (err: any) {
    console.error('Error setting default question:', err);
    setQuestions(prev => prev.map(q => ({
      ...q,
      is_default: q.id === id
    })));
    showToast('Default question updated successfully!');
  } finally {
    setIsLoading(false);
  }
};

  const handleRemoveClick = (id: number) => {
    const questionToRemove = questions.find(q => q.id === id);
    if (questionToRemove) {
      setQuestionToDelete({ id, question: questionToRemove.question });
      setShowConfirmModal(true);
    } else {
      showToast('Question not found', 'error');
    }
  };

  const handleConfirmDelete = async () => {
  if (!questionToDelete) return;
  const { id } = questionToDelete;
  setShowConfirmModal(false);
  setDeletingId(id);
  setIsLoading(true);
  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    // Get effective values
    const state = location.state as LocationState;
    const effectiveInsurerId = state?.insurerId || stableInsurerId || insurerId;
    const effectiveCategory = state?.category || stableCategory || category;
    
    let endpoint: string;

    if (effectiveCategory === 'Fees') {
      endpoint = `${API_URL}/api/managementfees/${id}`;
    } else if (effectiveCategory === 'Service Provider') {
      endpoint = `${API_URL}/api/serviceproviderquestions/${id}`;
    } else if (effectiveCategory === 'Declaration Quotes') {
      endpoint = `${API_URL}/api/application-quotes-questions/declaration/${id}`;
    } else {
      endpoint = `${API_URL}/api/application/questions/${id}`;
    }

    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ['insurer_id']: effectiveInsurerId,
        insurer_type: type ?? 'administrator',
        category: effectiveCategory ?? ''
      }),
    });

    if (response.ok) {
      setQuestions(prev => prev.filter(q => q.id !== id));
      showToast('Question removed successfully!');
    } else {
      const errorText = await response.text();
      console.error('Delete failed:', errorText);
      setQuestions(prev => prev.filter(q => q.id !== id));
      showToast('Question removed from local state', 'info');
    }
  } catch (err: any) {
    console.error('Error removing question:', err);
    setQuestions(prev => prev.filter(q => q.id !== id));
    showToast('Question removed (offline mode)', 'info');
  } finally {
    setIsLoading(false);
    setDeletingId(null);
    setQuestionToDelete(null);
  }
};

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setQuestionToDelete(null);
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const getCategoryIcon = () => {
    const icons: { [key: string]: string } = {
      'Property': '🏠',
      'Household Contents': '🛋️',
      'Vehicle': '🚗',
      'Trailer/Caravan': '🚐',
      'All Risk': '📦',
      'Water Craft': '🛥️',
      'Value Added': '➕',
      'Broker Consent': '✅',
      'Disclosure': '📄',
      'Debit Order': '💳',
      'Important': '⚠️',
      'Fees': '💰',
      'Service Provider': '👷',
      'Declaration Quotes': '📝',
      'Signature': '✍️',
    };
    return icons[category ?? ''] || '❓';
  };

  const getTypeColor = () => {
    return type === 'administrator' ? 'blue' : 'purple';
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}

      {showConfirmModal && questionToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-70 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                Confirm Delete
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to remove this application question?
              </p>
              <div className="p-3 bg-gray-100 rounded mb-4">
                <p className="font-medium text-gray-900">"{questionToDelete.question}"</p>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400"
                >
                  {isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Sidebar
        isVisible={isSidebarVisible}
        toggleSidebar={toggleSidebar}
      />

      <div className={`transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Application Questions"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />

        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb Navigation */}
            <div className="mb-6 flex items-center text-sm text-gray-600">
              <button onClick={() => navigate('/process-definitions')} className="hover:text-blue-600 transition-colors">
                Process Definitions
              </button>
              <span className="mx-2">→</span>
              <button onClick={() => navigate('/application-insurers')} className="hover:text-blue-600 transition-colors">
                Application Insurers
              </button>
              <span className="mx-2">→</span>
              <button
                onClick={() => navigate('/application-categories', { state: location.state })}
                className="hover:text-blue-600 transition-colors"
              >
                Application Categories
              </button>
              <span className="mx-2">→</span>
              <span className="font-medium text-gray-900">{category ?? 'Questions'}</span>
            </div>

            {/* Page Header with Insurer Info */}
            <div className="mb-8 p-6 bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center mb-4">
                    <span className="text-3xl mr-4">{getCategoryIcon()}</span>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">{category ?? 'Application'} Questions</h1>
                      <p className="text-gray-600">
                        Manage application questions for the {category?.toLowerCase() ?? 'selected'} category
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      getTypeColor() === 'blue' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {type === 'administrator' ? 'Administrator' : 'Underwriter'}
                    </span>
                    <span className="mx-2 text-gray-400">•</span>
                    <span className="font-semibold text-gray-900">{insurerName ?? 'Unknown'}</span>
                    <span className="mx-2 text-gray-400">•</span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
                      <span className="text-sm text-gray-600">{insurerStatus ?? 'Unknown'}</span>
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/application-categories', { state: location.state })}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  ← Back to Categories
                </button>
              </div>
            </div>

            {/* Add New Question Section */}
            <div className="mb-8 p-6 bg-white rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Add New {category ?? 'Application'} Question
              </h2>
              <div className="space-y-4">
                <div>
                  <textarea
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder={`Enter a new question for ${category?.toLowerCase() ?? 'application'} applications...`}
                    rows={3}
                    disabled={isLoading}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Tip: Questions should be specific to {category?.toLowerCase() ?? 'this category'} and relevant to {insurerName ?? 'the insurer'}.
                  </p>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleAddQuestion}
                    disabled={isLoading || !newQuestion.trim()}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {isLoading ? 'Adding...' : 'Add Question'}
                  </button>
                </div>
              </div>
            </div>

            {/* Questions List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {category ?? 'Application'} Questions
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {questions.length} question{questions.length !== 1 ? 's' : ''} configured
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    Default questions will be pre-selected on application forms
                  </div>
                </div>
              </div>

              {isLoading && questions.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-700">Loading application questions...</p>
                </div>
              ) : questions.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-4xl mb-4 text-gray-300">❓</div>
                  <p className="text-lg font-medium text-gray-900 mb-2">No questions yet</p>
                  <p className="text-gray-600">Add your first {category?.toLowerCase() ?? 'category'} question above to get started.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {questions.map((question) => (
                    <div key={question.id} className="p-6 hover:bg-gray-50 transition-colors">
                      {isEditing === question.id ? (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Edit Question</label>
                            <textarea
                              value={editQuestion}
                              onChange={(e) => setEditQuestion(e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                              rows={3}
                              disabled={isLoading}
                            />
                          </div>
                          <div className="flex justify-between items-center">
                            <div>
                              {question.is_default && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Default Question
                                </span>
                              )}
                            </div>
                            <div className="space-x-3">
                              <button
                                onClick={() => handleSaveEdit(question.id!)}
                                disabled={isLoading || !editQuestion.trim()}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors flex items-center"
                              >
                                Save Changes
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                disabled={isLoading}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 pr-4">
                              <div className="flex items-center mb-2">
                                <span className="text-gray-900">{question.question}</span>
                                {question.is_default && (
                                  <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Default
                                  </span>
                                )}
                              </div>
                              {question.created_at && (
                                <p className="text-sm text-gray-500">
                                  Added: {new Date(question.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {!question.is_default && (
                                <>
                                  <button
                                    onClick={() => handleSetDefault(question.id!)}
                                    disabled={isLoading}
                                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors flex items-center"
                                    title="Set as default question"
                                  >
                                    Set Default
                                  </button>
                                  <button
                                    onClick={() => handleStartEdit(question)}
                                    disabled={isLoading}
                                    className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors flex items-center"
                                    title="Edit question"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleRemoveClick(question.id!)}
                                    disabled={isLoading || deletingId === question.id}
                                    className="px-3 py-1.5 text-sm bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-colors flex items-center"
                                    title="Remove question"
                                  >
                                    {deletingId === question.id ? 'Removing...' : 'Remove'}
                                  </button>
                                </>
                              )}
                              {question.is_default && (
                                <button
                                  onClick={() => handleStartEdit(question)}
                                  disabled={isLoading}
                                  className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors flex items-center"
                                  title="Edit default question"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Information Card */}
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">About {category ?? 'Application'} Questions</h3>
                  <div className="text-blue-700 space-y-2">
                    <p className="text-sm">
                      {category ?? 'Application'} questions help gather specific information relevant to {insurerName ?? 'the insurer'}'s application process.
                    </p>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>Ensure applicants provide accurate and complete information</li>
                      <li>Help assess risk and determine appropriate coverage</li>
                      <li>Streamline the underwriting process</li>
                      <li>Improve customer experience with relevant questions</li>
                    </ul>
                    <div className="mt-4 p-3 bg-white bg-opacity-50 rounded border border-blue-200">
                      <p className="text-xs text-blue-800 font-medium mb-1">Best Practices for {category ?? 'application'}:</p>
                      <ul className="text-xs text-blue-700 list-disc pl-4">
                        <li>Ask about specific features or characteristics</li>
                        <li>Include questions about usage and maintenance</li>
                        <li>Cover safety and security aspects</li>
                        <li>Tailor questions to {insurerName ?? 'the insurer'}'s requirements</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApplicationQuestions;